import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { ModuleAccessEditor } from '../components/ModuleAccessEditor';
import { ALL_SLUGS } from '../config/nav';
import { useAuth } from '../lib/auth';

/**
 * Staff Groups.
 *
 * Laid out like click2gro's Job Titles: a searchable, paginated list of roles,
 * each opened in a dialog that holds its name and which modules it can open.
 * The legacy screen stored a PHP serialize()d array of controller class names —
 * which was never read for two of the three roles, because check_permission()
 * bypassed it for levels 1 and 12. This is rows in role_permissions, and it is
 * actually consulted.
 */

interface StaffGroup {
    readonly id: number;
    readonly name: string;
    readonly description: string | null;
    readonly isDefault: boolean;
    readonly isActive: boolean;
    readonly staffCount: number;
    readonly activeStaffCount: number;
    readonly permissions: readonly string[];
    readonly bypassesChecks: boolean;
}

interface FormState {
    readonly name: string;
    readonly description: string;
    readonly isDefault: boolean;
    readonly permissions: readonly string[];
}

const EMPTY_FORM: FormState = { name: '', description: '', isDefault: false, permissions: [] };

function describeModules(g: StaffGroup): string {
    if (g.bypassesChecks) {
        return 'All modules (bypasses checks)';
    }
    const held = ALL_SLUGS.filter((slug) => g.permissions.includes(slug)).length;
    if (held === ALL_SLUGS.length) {
        return 'All modules';
    }
    if (held === 0) {
        return 'No modules';
    }
    return `${String(held)} of ${String(ALL_SLUGS.length)}`;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
    return a.length === b.length && a.every((x) => b.includes(x));
}

export function StaffGroups(): React.JSX.Element {
    const theme = useTheme();
    const { me } = useAuth();
    const [groups, setGroups] = useState<readonly StaffGroup[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<StaffGroup | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<StaffGroup | null>(null);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        let cancelled = false;
        apiFetch<{ groups: readonly StaffGroup[] }>('/staff-groups')
            .then((r) => {
                if (!cancelled) {
                    setGroups(r.groups);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load staff groups');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return groups.filter(
            (g) =>
                needle === '' ||
                g.name.toLowerCase().includes(needle) ||
                (g.description ?? '').toLowerCase().includes(needle),
        );
    }, [groups, search]);
    const visible = filtered.slice(page * pageSize, (page + 1) * pageSize);

    const isOwnRole = (g: StaffGroup | null): boolean => g !== null && me?.staffGroupId === g.id;
    // Super Admin has no rows to edit, and editing your own role's access from
    // a screen that role grants you is how an administrator locks themselves
    // out. The API refuses both; this keeps the controls out of reach.
    const permissionsLocked = editing !== null && (editing.bypassesChecks || isOwnRole(editing));

    function openAdd(): void {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormError(null);
        setFormOpen(true);
    }

    function openEdit(g: StaffGroup): void {
        setEditing(g);
        setForm({
            name: g.name,
            description: g.description ?? '',
            isDefault: g.isDefault,
            permissions: g.bypassesChecks ? [] : g.permissions,
        });
        setFormError(null);
        setFormOpen(true);
    }

    function closeForm(): void {
        if (saving) {
            return;
        }
        setFormOpen(false);
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormError(null);
    }

    async function persist(): Promise<void> {
        const name = form.name.trim();
        const description = form.description.trim();

        if (editing === null) {
            const created = await apiFetch<StaffGroup>('/staff-groups', {
                method: 'POST',
                body: JSON.stringify({ name, description }),
            });
            if (form.isDefault) {
                await apiFetch<unknown>(`/staff-groups/${String(created.id)}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ isDefault: true }),
                });
            }
            if (form.permissions.length > 0) {
                await apiFetch<unknown>(`/staff-groups/${String(created.id)}/permissions`, {
                    method: 'PUT',
                    body: JSON.stringify({ permissions: form.permissions }),
                });
            }
            return;
        }

        const changes: Record<string, unknown> = {};
        if (name !== editing.name) {
            changes['name'] = name;
        }
        if (description !== (editing.description ?? '')) {
            changes['description'] = description;
        }
        if (form.isDefault !== editing.isDefault) {
            changes['isDefault'] = form.isDefault;
        }
        if (Object.keys(changes).length > 0) {
            await apiFetch<unknown>(`/staff-groups/${String(editing.id)}`, {
                method: 'PATCH',
                body: JSON.stringify(changes),
            });
        }
        if (!permissionsLocked && !sameSet(form.permissions, editing.permissions)) {
            await apiFetch<unknown>(`/staff-groups/${String(editing.id)}/permissions`, {
                method: 'PUT',
                body: JSON.stringify({ permissions: form.permissions }),
            });
        }
    }

    function submit(): void {
        if (form.name.trim() === '') {
            setFormError('Name is required');
            return;
        }
        setSaving(true);
        setFormError(null);
        const wasNew = editing === null;
        persist()
            .then(() => {
                if (wasNew) {
                    // So the new group is visible straight away.
                    setSearch('');
                    setPage(0);
                }
                setFormOpen(false);
                setEditing(null);
                reload();
            })
            .catch((err: unknown) => {
                setFormError(err instanceof Error ? err.message : 'Could not save the group');
                // A new group may have been created before a later step failed.
                reload();
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function confirmDelete(): void {
        if (deleting === null) {
            return;
        }
        setSaving(true);
        apiFetch<unknown>(`/staff-groups/${String(deleting.id)}`, { method: 'DELETE' })
            .then(() => {
                setDeleting(null);
                reload();
            })
            .catch((err: unknown) => {
                setDeleting(null);
                setError(err instanceof Error ? err.message : 'Could not delete the group');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function deleteBlockedReason(g: StaffGroup): string | null {
        if (g.bypassesChecks) {
            return 'Super Admin is built in and cannot be deleted';
        }
        if (isOwnRole(g)) {
            return 'You cannot delete your own group';
        }
        if (g.staffCount > 0) {
            return `${String(g.staffCount)} staff are in this group — move them first`;
        }
        return null;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Staff Groups" subtitle="Create and manage staff groups, and which modules each can open" />

            {error !== null ? (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => {
                        setError(null);
                    }}
                >
                    {error}
                </Alert>
            ) : null}

            <Box
                sx={{
                    mb: 3,
                    display: 'flex',
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                }}
            >
                <TextField
                    placeholder="Search groups..."
                    size="small"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    slotProps={{
                        input: { startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> },
                    }}
                    sx={{ flex: 1, maxWidth: { xs: '100%', sm: 300 } }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
                    Add Group
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 600, '& th, & td': { whiteSpace: 'nowrap' } }}>
                    <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Staff</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Modules</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    {search !== ''
                                        ? 'No staff groups match your search.'
                                        : 'No staff groups yet. Click "Add Group" to create one.'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            visible.map((g) => {
                                const modules = describeModules(g);
                                const blocked = deleteBlockedReason(g);
                                return (
                                    <TableRow key={g.id} hover>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {g.name}
                                                </Typography>
                                                {g.isDefault ? <Chip label="Default" size="small" /> : null}
                                                {isOwnRole(g) ? (
                                                    <Chip label="Your group" size="small" variant="outlined" />
                                                ) : null}
                                            </Stack>
                                            {g.description !== null ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    {g.description}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {g.staffCount}
                                                {g.activeStaffCount !== g.staffCount ? (
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {` (${String(g.activeStaffCount)} active)`}
                                                    </Typography>
                                                ) : null}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color={modules === 'No modules' ? 'text.disabled' : 'text.primary'}
                                            >
                                                {modules}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        disabled={saving}
                                                        onClick={() => {
                                                            openEdit(g);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {blocked !== null ? (
                                                    <Tooltip title={blocked}>
                                                        <span>
                                                            <IconButton size="small" disabled>
                                                                <LockIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            disabled={saving}
                                                            sx={{ color: 'error.main' }}
                                                            onClick={() => {
                                                                setDeleting(g);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filtered.length}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(_event, next) => {
                    setPage(next);
                }}
                onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            {/* The single most useful thing this screen can say, given what the
                legacy permission data turned out to be. */}
            <Alert severity="info" sx={{ mt: 3, fontSize: '0.78rem' }}>
                The old permission lists were not migrated. They were never read for System Administrator or Super Admin
                — <code>check_permission()</code> returned true for both before reaching them — and Employee&apos;s list
                held one entry, <code>Backend_invoicing</code>. No role held <code>Backend_timesheet</code> at all, so
                the Timesheet screen was unreachable for 57 of 59 staff. These defaults are a fresh start.
            </Alert>

            {/* Add / Edit */}
            <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
                <DialogTitle>{editing !== null ? 'Edit Staff Group' : 'Add New Staff Group'}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {formError !== null ? (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            onClose={() => {
                                setFormError(null);
                            }}
                        >
                            {formError}
                        </Alert>
                    ) : null}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label="Name"
                            required
                            fullWidth
                            autoFocus
                            value={form.name}
                            disabled={saving}
                            placeholder="e.g., Office Manager, Foreman"
                            onChange={(e) => {
                                setForm((f) => ({ ...f, name: e.target.value }));
                            }}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.description}
                            disabled={saving}
                            onChange={(e) => {
                                setForm((f) => ({ ...f, description: e.target.value }));
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isDefault}
                                    // Exactly one default: it moves by ticking another group, not by unticking this one.
                                    disabled={saving || editing?.isDefault === true}
                                    onChange={(e) => {
                                        setForm((f) => ({ ...f, isDefault: e.target.checked }));
                                    }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">Default group for new staff</Typography>
                                    {editing?.isDefault === true ? (
                                        <Typography variant="caption" color="text.secondary">
                                            To change the default, tick it on another group
                                        </Typography>
                                    ) : null}
                                </Box>
                            }
                        />

                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Module Access
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                                {editing?.bypassesChecks === true
                                    ? 'Super Admin bypasses permission checks and can open every module. This cannot be restricted.'
                                    : isOwnRole(editing)
                                      ? 'This is your own group. Someone in another administrator group has to change its access, so you cannot lock yourself out.'
                                      : 'Staff in this group can only open the modules ticked here.'}
                            </Typography>
                            <ModuleAccessEditor
                                value={form.permissions}
                                onChange={(next) => {
                                    setForm((f) => ({ ...f, permissions: next }));
                                }}
                                disabled={saving || isOwnRole(editing)}
                                locked={editing?.bypassesChecks === true}
                            />
                        </Box>

                        {editing !== null && editing.staffCount > 0 && !permissionsLocked ? (
                            <Typography variant="caption" color="text.secondary">
                                Changes apply to all {editing.staffCount} staff in this group.
                            </Typography>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeForm} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={submit} disabled={saving}>
                        {editing !== null ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete */}
            <Dialog
                open={deleting !== null}
                onClose={() => {
                    if (!saving) {
                        setDeleting(null);
                    }
                }}
            >
                <DialogTitle>Delete Staff Group</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{deleting?.name}</strong>? Its module access is removed
                        with it.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={saving}
                        onClick={() => {
                            setDeleting(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" disabled={saving} onClick={confirmDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
